import {
  Alert,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useMemo, useCallback } from 'react';
import { StudentUserDetail } from './StudentUserDetail';
import { getUserFullName } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { StudentEnrolments } from './StudentEnrolments';
import { StudentSettings } from './StudentSettings';
import { StudentRecentActivity } from './StudentRecentActivity';
import { useAuthTenant } from '../useAuthTenant';
import { SupportCaseload } from './SupportCaseload';
import { SupportStudent } from './SupportStudent';
import { IdCard } from 'lucide-react';
import { skipToken } from '@tanstack/react-query';

export function StudentDetailPage() {
  const { studentId, tab, value } = useParams<{
    studentId: string;
    tab: string;
    value: string;
  }>();
  const auth = useAuthTenant();
  const navigate = useNavigate();

  const tabs = useMemo(
    () => [
      { value: 'enrolments', label: t`Enrolments` },
      { value: 'chats', label: t`Participant Chat` },
      { value: 'support', label: t`Duke Chat` },
      { value: 'details', label: t`Personal details` },
      { value: 'settings', label: t`Settings` },
      { value: 'audit-logs', label: t`Audit Logs` },
    ],
    [],
  );

  const selectedTab = useMemo(() => {
    if (typeof tab !== 'string') {
      return 0;
    }

    const tabIdx = tabs.findIndex((tabObj) => tabObj.value === tab);
    return tabIdx !== -1 ? tabIdx : 0;
  }, [tab, tabs]);

  const handleTabsChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        navigate(`/student/${studentId}/${tabs[index].value}`);
      }
    },
    [navigate, studentId, tabs],
  );

  const { data, isLoading } = trpcTenant.users.studentById.useQuery(
    studentId != null ? { id: studentId } : skipToken,
    { enabled: Boolean(studentId) },
  );

  const { data: consultant } = trpcTenant.users.consultantById.useQuery(
    data?.studentData?.owned_by != null ? { id: data.studentData.owned_by } : skipToken,
    { enabled: Boolean(data?.studentData?.owned_by) },
  );

  if (studentId == null) {
    return null;
  }

  if (isLoading || !data) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (data == null) {
    return (
      <Alert>
        <Trans>No participant found</Trans>
      </Alert>
    );
  }

  const filteredTabs =
    auth.user.role !== 'CONSULTANT' ? tabs : tabs.filter((tab) => tab.value !== 'settings');

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        p={{ base: 2, md: 4 }}
      >
        <HStack justify="space-between" alignContent="center">
          <HStack align="start" h="full" alignSelf="stretch">
            <UserAvatar
              user={{
                first_name: data?.first_name,
                last_name: data?.last_name,
                picture_url: data?.picture_url,
              }}
              size="lg"
            />
            <VStack ml="16px" align={'start'} spacing={0}>
              <Text fontWeight="bold">
                {getUserFullName({
                  first_name: data?.first_name,
                  last_name: data?.last_name,
                  picture_url: data?.picture_url,
                })}
              </Text>

              <HStack
                gap="16px"
                align="center"
                justify="start"
                flex="1"
                fontSize="sm"
                lineHeight="5"
                fontWeight="medium"
                color="fg.muted"
              >
                {data?.studentData?.universal_student_identifier != null ? (
                  <HStack align="center" gap="4px">
                    <Icon as={IdCard} color="fg.subtle" boxSize="20px" />
                  </HStack>
                ) : null}
              </HStack>
            </VStack>
          </HStack>
        </HStack>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={selectedTab}
          onChange={handleTabsChange}
        >
          <TabList bg="bg.subtle">
            {filteredTabs.map((tab, index) => (
              <Tab
                key={tab.value}
                bg={selectedTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels width="100%" height="100%" overflow="auto">
            {filteredTabs.map((tab) => (
              <TabPanel key={tab.value} width="100%" height="100%" overflow="auto" p={0}>
                {data != null ? (
                  <>
                    {tab.value === 'enrolments' && <StudentEnrolments student={data} />}
                    {tab.value === 'chats' && (
                      <SupportStudent
                        studentDrn={data.drn}
                        selectedChat={selectedTab === 1 ? value : undefined}
                      />
                    )}
                    {tab.value === 'support' && (
                      <SupportCaseload
                        studentDrn={data.drn}
                        selectedChat={selectedTab === 2 ? value : undefined}
                      />
                    )}
                    {tab.value === 'details' && <StudentUserDetail student={data} />}
                    {tab.value === 'settings' ? <StudentSettings student={data} /> : null}
                    {tab.value === 'audit-logs' && (
                      <StudentRecentActivity studentId={studentId} consultant={consultant} />
                    )}
                  </>
                ) : null}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
